import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { rem } from "polished";
import React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link, useLocation } from "react-router-dom";
import { useRecoilValueLoadable } from "recoil";
import { useAccount as useWagmiAccount } from "wagmi";

import { initStateAtom } from "../../atoms";
import { Chevron, NavLink, Spacer } from "../../base";
import { formatEthAccount } from "../../lib/format";
import { useAccount, useConnectorMeta } from "../../modules/hooks";
import i18n from "../../modules/i18n";

const LogoImage = styled.img`
  max-width: 100%;
  max-height: 100%;
`;

const LogoImageContainer = styled.div`
  width: ${rem(18)};
  height: ${rem(18)};

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: ${rem(6)};
  overflow: hidden;
`;

const ConnectorLogo = () => {
  const [, walletLogo] = useConnectorMeta();

  return walletLogo ? (
    <LogoImageContainer>
      <LogoImage src={walletLogo} />
    </LogoImageContainer>
  ) : null;
};

const useInitState = () => {
  const loadable = useRecoilValueLoadable(initStateAtom);
  const { state, contents } = loadable;
  switch (state) {
    case "hasValue": {
      return contents as { loaded: boolean; ok: boolean };
    }
    case "hasError": {
      return { loaded: true, ok: false };
    }
  }
  return { loaded: false, ok: false };
};

interface AccountButtonProps {
  menuOpen?: boolean;
}

const RealNavLink = NavLink.withComponent(Link);

const AccountButton = ({ menuOpen = false }: AccountButtonProps) => {
  const theme = useTheme();
  const account = useAccount();

  return (
    <RealNavLink
      css={css`
        display: flex;
        align-items: center;
        color: ${theme.color.end};
      `}
      to={menuOpen ? "/" : "/account"}
    >
      <ConnectorLogo />
      <Spacer col size={16} />
      {formatEthAccount(account)}
      <Spacer col size={16} />
      <Chevron
        color={theme.color.end}
        css={css`
          transform: rotate(${menuOpen ? "-90" : "90"}deg);
          width: ${rem(10)};
          height: ${rem(20)};
          fill: none;
          margin-top: -${rem(1)};
        `}
      />
    </RealNavLink>
  );
};

const AccountWidget = () => {
  const { color } = useTheme();
  const location = useLocation();
  const { isConnected: active } = useWagmiAccount();
  const { loaded, ok } = useInitState();

  if (!active || !ok) {
    return (
      <RealNavLink
        css={css`
          &:hover {
            color: ${color.end};
          }
        `}
        to={loaded ? "/connect" : "/"}
      >
        {i18n.t<string>("buttons.connect")}
      </RealNavLink>
    );
  }
  return (
    <ErrorBoundary fallbackRender={() => <label>Error</label>}>
      <React.Suspense fallback={null}>
        <AccountButton menuOpen={location.pathname === "/account"} />
      </React.Suspense>
    </ErrorBoundary>
  );
};

export default AccountWidget;
